import React from 'react'

export type ClockMatrixExtraColumn = {
  key: string
  header: string
  /** Per-row display value keyed by row id. */
  values: Record<string, string>
}

type Props = {
  locations: string[]
  rows: Array<{ id: string; label: string }>
  /** rowId → location → value */
  values: Record<string, Record<string, string>>
  onChange: (rowId: string, location: string, value: string) => void
  extraColumns?: ClockMatrixExtraColumn[]
  className?: string
}

/**
 * Shared Floor Sheet clock grid: row labels | clock columns | optional Min/Max/Avg.
 * Matches the Preheat / Interpass interaction pattern.
 */
export default function FloorSheetClockMatrix({
  locations,
  rows,
  values,
  onChange,
  extraColumns = [],
  className = ''
}: Props) {
  return (
    <div className={`mt-3 overflow-x-auto ${className}`}>
      <table className="w-full min-w-[520px] text-sm text-text-grey">
        <thead>
          <tr className="bg-[#FAFBFC] text-xs uppercase tracking-wide text-heritage-grey">
            <th className="px-2 py-2 text-left">Pass / Row</th>
            {locations.map((loc) => (
              <th key={loc} className="px-2 py-2 text-center whitespace-nowrap">
                {loc}
              </th>
            ))}
            {extraColumns.map((col) => (
              <th key={col.key} className="px-2 py-2 text-center">
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id} className="border-t border-[#EEF0F3]">
              <td className="px-2 py-2 text-xs font-medium whitespace-nowrap">{row.label}</td>
              {locations.map((loc) => (
                <td key={loc} className="px-1 py-1">
                  <input
                    className="input-primary h-8 w-full min-w-[3.5rem] text-center text-xs"
                    value={values[row.id]?.[loc] ?? ''}
                    onChange={(e) => onChange(row.id, loc, e.target.value)}
                    aria-label={`${row.label} ${loc}`}
                  />
                </td>
              ))}
              {extraColumns.map((col) => (
                <td
                  key={col.key}
                  className="px-2 py-2 text-center text-xs text-heritage-grey"
                  data-testid={`clock-extra-${row.id}-${col.key}`}
                >
                  {col.values[row.id] || '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Numeric min / max / avg over non-blank clock cells (text like "Flush" ignored). */
export function aggregateClockRow(values: Record<string, string>): {
  min: string
  max: string
  avg: string
} {
  const nums: number[] = []
  for (const raw of Object.values(values ?? {})) {
    const t = String(raw ?? '').trim()
    if (!t) continue
    const n = Number(t)
    if (Number.isFinite(n)) nums.push(n)
  }
  if (nums.length === 0) return { min: '', max: '', avg: '' }
  const sum = nums.reduce((a, b) => a + b, 0)
  return {
    min: String(Math.min(...nums)),
    max: String(Math.max(...nums)),
    avg: String(Number((sum / nums.length).toFixed(3)))
  }
}
